import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, MapPin } from 'lucide-react';
import PillarLayout from './PillarLayout';

const CANONICAL = 'https://enbet-consulting.de/ki-beratung-kassel';

const faqItems = [
  {
    q: 'Wer bietet KI-Beratung in Kassel und Nordhessen an?',
    a: 'In der Region Kassel/Nordhessen gibt es einige IT- und Digitalisierungs-Consultancies (let\'s dev, BTQ Kassel u. a.), die KI auch im Angebot haben – aber als Querschnittsthema. ENBET Consulting (Entertain Better) ist als spezialisierte Boutique-Beratung positioniert: ausschließlich für stationären Handel, Filialisten und Franchise-Systeme. Standort Schauenburg (Nordhessen), Audits remote, Workshops und Integration vor Ort oder hybrid – in ganz Deutschland.',
  },
  {
    q: 'Welche Kassel-Unternehmen profitieren am meisten von KI-Beratung?',
    a: 'Vier Typen aus Nordhessen sehen typischerweise den schnellsten ROI: (1) Lokale Filialisten und Einzelhändler mit mehreren Standorten in Kassel und Umgebung. (2) Familiengeführte Mittelständler aus Handel und Dienstleistung mit klassischen Frontline-Teams. (3) Franchise-Systeme mit Sitz oder Vertretungen in der Region. (4) Serviceintensive stationäre Betriebe, die Wettbewerbsdruck durch Online-Anbieter spüren und ihre menschliche Stärke verteidigen wollen.',
  },
  {
    q: 'Wie funktioniert KI-Beratung lokal vor Ort vs. remote?',
    a: 'Der KI Ready Check (Modul 1) läuft komplett remote ab – effizient, ortsunabhängig, deutlich günstiger als klassische Vor-Ort-Audits. Der KI Strategie Workshop (Modul 2) wird als Tagesformat angeboten: vor Ort bei euch in Kassel/Nordhessen, in Sebastian\'s Büroumgebung in Schauenburg, oder hybrid. Die Integration (Modul 3) erfolgt projektbasiert mit klar definierten Vor-Ort-Terminen, ergänzt durch Remote-Begleitung im Tagesgeschäft.',
  },
  {
    q: 'Was unterscheidet ENBET von klassischen IT-Beratern in Kassel?',
    a: 'Drei Punkte. (1) Branchen-Spezialisierung: ENBET arbeitet ausschließlich mit stationärem Handel und Filialisten – Sebastian Funke hat 100+ Retail-Standorte skaliert und ca. 9 Mio. Monatsumsatz im Retail-Setup aufgebaut. Das ist echtes operatives Wissen, kein generisches IT-Beratungs-Portfolio. (2) Boutique-Format: wenige Kunden gleichzeitig, persönliche Begleitung. (3) Methodik: ROAD × ROME-Framework – nur messbare Ergebnisse zählen, kein Tool-Verkauf, kein Berater-Selbstzweck.',
  },
  {
    q: 'Wie buche ich einen Termin in Kassel?',
    a: 'Der erste Schritt ist immer ein kostenloser 20-Minuten Potenzial-Check. Termin direkt über die Homepage buchbar – Bestätigung sofort, Google-Meet-Link wird automatisch gesendet. Wer den Workshop vor Ort in Kassel oder Schauenburg wünscht, kann das direkt im Gespräch klären. Reisetermine in der Region (Kassel, Marburg, Göttingen, Paderborn, Fulda) sind unkompliziert organisierbar.',
  },
];

const longTailFaq = [
  {
    q: 'Welche Branchen in Nordhessen sind besonders KI-affin?',
    a: 'Aus unserer Erfahrung mit der Region: lokale Filialisten (Bäckereien, Apotheken, Optiker, Mode), Gastronomie-Ketten, Handwerksbetriebe mit Frontline-Service, sowie produzierende Mittelständler mit Vertriebs-Außendienst. Gemeinsamer Nenner: hohe Kundennähe, viele Routine-Prozesse, mehrere Standorte. Genau dort wirkt KI als Entlastung am stärksten.',
  },
  {
    q: 'Kann ENBET auch außerhalb von Kassel arbeiten?',
    a: 'Ja. Der Standort Schauenburg/Kassel ist die Heimatbasis, das Einzugsgebiet ist Deutschland. Audits laufen remote, Workshops können in eurem Betrieb bundesweit stattfinden. Reisetage werden transparent kalkuliert und im Festpreis-Modell der Module bereits berücksichtigt.',
  },
  {
    q: 'Gibt es lokale Referenzen aus Kassel/Nordhessen?',
    a: 'Sebastian hat seine Retail-Erfahrung in skalierten Filialstrukturen aufgebaut, mit operativem Hintergrund in Service-Optimierung und Standort-Steuerung. Konkrete Kundenreferenzen werden auf Anfrage im Erstgespräch geteilt – aus Diskretionsgründen veröffentlichen wir keine Namen ohne Zustimmung. Im 20-Minuten Potenzial-Check zeigt Sebastian dir passende Beispiele für deine Branche.',
  },
];

export default function PillarBeratungKassel() {
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${CANONICAL}#article`,
        headline: 'KI-Beratung Kassel – Boutique-Beratung für stationären Handel in Nordhessen',
        description: 'Spezialisierte KI-Beratung in Kassel und Nordhessen für stationären Einzelhandel, Filialisten und Franchise-Systeme. Boutique-Format, ROAD × ROME-Methodik, deutschlandweit verfügbar.',
        author: { '@id': 'https://enbet-consulting.de/#sebastian-funke' },
        publisher: { '@id': 'https://enbet-consulting.de/#organization' },
        mainEntityOfPage: CANONICAL,
        inLanguage: 'de-DE',
        about: 'KI-Beratung Kassel',
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${CANONICAL}#localbusiness`,
        name: 'ENBET Consulting – KI-Beratung Kassel',
        url: CANONICAL,
        telephone: '+4917623943896',
        email: 'info@enbet-consulting.de',
        priceRange: '€€€',
        image: 'https://enbet-consulting.de/assets/logo.png',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Im Tal 30',
          postalCode: '34270',
          addressLocality: 'Schauenburg',
          addressRegion: 'Hessen',
          addressCountry: 'DE',
        },
        areaServed: ['Kassel', 'Nordhessen', 'Hessen', 'Marburg', 'Göttingen', 'Paderborn', 'Fulda', 'Deutschland'],
        description: 'KI-Beratung in Kassel und Nordhessen, spezialisiert auf stationären Einzelhandel, Filialisten und Franchise-Systeme. Audits remote, Workshops vor Ort oder hybrid.',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI im stationären Handel', item: 'https://enbet-consulting.de/ki-im-stationaeren-handel' },
          { '@type': 'ListItem', position: 3, name: 'KI-Beratung Kassel', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [...faqItems, ...longTailFaq].map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI-Beratung Kassel – Boutique für stationären Handel in Nordhessen | ENBET Consulting"
      description="KI-Beratung in Kassel und Nordhessen für stationären Einzelhandel, Filialisten und Franchise-Systeme. Boutique-Format, ROAD × ROME-Methodik. Kostenloser 20-Minuten Potenzial-Check."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold flex items-center gap-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <MapPin size={14} /> Standort Kassel / Nordhessen
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.15] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            KI-Beratung Kassel – Boutique für stationären Handel
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
            ENBET Consulting (Entertain Better) ist die spezialisierte KI-Beratung für stationären Einzelhandel und Filialisten – ansässig in Schauenburg bei Kassel, tätig in ganz Deutschland. Boutique-Format, klare Methodik, ehrliche Einschätzung statt Folien.
          </motion.p>
        </div>
      </section>

      {/* Kurzantwort */}
      <section className="bg-[#f7f7f7] py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kurzantwort
          </p>
          <p className="text-[#1f1f1f] text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            ENBET ist die einzige KI-Beratung in Nordhessen mit ausschließlichem Fokus auf stationären Handel, Filialisten und Franchise-Systeme. Standort Schauenburg (10 Min. von Kassel). Audits remote, Workshops vor Ort oder hybrid, Integration deutschlandweit. Gründer Sebastian Funke kommt aus 100+ skalierten Retail-Standorten – keine generische IT-Beratung, sondern echtes operatives Wissen mit klarer Methodik (ROAD × ROME). Erster Termin: kostenloser 20-Minuten Potenzial-Check.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-16">
          {faqItems.map((item, i) => (
            <article key={i} className="scroll-mt-24" id={`q-${i + 1}`}>
              <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.q}
              </h2>
              <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {item.a}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Standort-Block */}
      <section className="bg-[#0a0a0a] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Standort
          </p>
          <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            ENBET in Schauenburg bei Kassel
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-white/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <div>
              <p className="text-white font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Adresse</p>
              <p>ENBET Consulting<br />Im Tal 30<br />34270 Schauenburg<br />Deutschland</p>
            </div>
            <div>
              <p className="text-white font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Einzugsgebiet</p>
              <p>Kassel · Marburg · Göttingen · Paderborn · Fulda · Bad Hersfeld – plus deutschlandweite Projektarbeit. Anfahrt aus Kassel-Mitte ca. 15 Min.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mini-FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Folgefragen
          </h2>
          <div className="space-y-8">
            {longTailFaq.map((item, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.q}
                </h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verwandte Pillars */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Weiter vertiefen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel', sub: 'Grundlagen, Use-Cases und Kosten – der Pillar-Artikel.' },
              { to: '/ki-fuer-filialisten', label: 'KI für Filialisten', sub: 'Multi-Standort-Strategien und Franchise-Logik.' },
              { to: '/ki-strategie-retail', label: 'KI-Strategie Retail', sub: 'Roadmap und ROI-Kalkulation aus dem Workshop.' },
            ].map((link, i) => (
              <Link key={i} to={link.to} className="border border-[#e5e5e5] p-6 hover:border-[#befa0a] transition-colors flex flex-col gap-3">
                <ArrowRight size={18} className="text-[#befa0a]" />
                <span className="text-[#1f1f1f] text-base font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>{link.label}</span>
                <span className="text-[#1f1f1f]/60 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{link.sub}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Erstgespräch in Kassel: kostenloser 20-Minuten Potenzial-Check
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Direkter Austausch mit Sebastian Funke – ehrliche Einschätzung, kein Verkaufsdruck. Remote oder vor Ort, ganz wie es für dich passt.
          </p>
          <Link to="/#booking" className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Check size={16} /> Termin vereinbaren
          </Link>
        </div>
      </section>
    </PillarLayout>
  );
}
